import { Typography } from "@outschool/backpack";
import {
  CreateNewCollectionMutation,
  CreateNewCollectionMutationVariables
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { gql, useMutation } from "@outschool/ui-apollo";
import {
  FormField,
  Input,
  Modal,
  ModalActionButtons,
  Space
} from "@outschool/ui-legacy-component-library";
import React from "react";

import { useListCurrentUserSavedClassLists } from "./graphql/hooks";
import { useSaveToCollections } from "./graphql/mutations";
import useEditableCollectionName from "./useEditableCollectionName";

const CreateNewCollection = gql`
  mutation CreateNewCollection($name: String!) {
    createSavedClassList(name: $name) {
      uid
      name
    }
  }
`;

export default function CreateNewCollectionModal({
  open,
  onCancel,
  onCreated,
  activityUid
}: {
  open: boolean;
  onCancel: () => void;
  onCreated: () => void;
  activityUid: string;
}) {
  const { t } = useTranslation(
    "client\\components\\saving\\CreateNewCollectionModal"
  );

  const { loading, savedClassLists } = useListCurrentUserSavedClassLists();
  const {
    collectionName,
    errorMessage,
    isCollectionNameValid,
    setCollectionName
  } = useEditableCollectionName(savedClassLists);

  const [createNewCollection] = useMutation<
    CreateNewCollectionMutation,
    CreateNewCollectionMutationVariables
  >(CreateNewCollection);
  const saveToCollections = useSaveToCollections(activityUid);
  const [savingInProgress, setSavingInProgress] = React.useState(false);

  const onCreateCollectionClick = React.useCallback(async () => {
    setSavingInProgress(true);
    try {
      const name = collectionName?.trim();
      const result = await createNewCollection({
        // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
        variables: { name },
        // TODO change this to an `update` statement
        refetchQueries: ["ListCurrentUserSavedClassLists"]
      });
      // @ts-ignore TS(2532): Object is possibly 'undefined'.
      const newListUid = result.data.createSavedClassList.uid;
      await saveToCollections([newListUid]);
      onCreated();
    } finally {
      setSavingInProgress(false);
    }
  }, [saveToCollections, onCreated, createNewCollection, collectionName]);

  if (loading) {
    return null;
  }

  return (
    <Modal open={open} hasCloseButton={false} onClose={onCancel}>
      <Typography variant="h3">{t`Create a new list`}</Typography>
      <FormField error={errorMessage}>
        <Input
          placeholder={t`Name your list`}
          value={collectionName}
          onChange={setCollectionName}
        />
      </FormField>
      <Space y="medium" />
      <ModalActionButtons
        cancelLabel={t`Cancel`}
        primaryLabel={t`Create List`}
        onCancel={onCancel}
        onPrimary={onCreateCollectionClick}
        disabled={!isCollectionNameValid || savingInProgress}
      />
    </Modal>
  );
}
